import { useMutation } from '@tanstack/react-query';

import { apiClient } from '@api/client';
import { LanguageCode } from '@libs/constants';
import {
  ClassifyDocumentsAcceptedResponse,
  GetClassificationStatusResponse,
} from '@libs/types';

/**
 * Request payload for document classification API
 */
interface ClassifyDocumentsRequest {
  documentIds: string[];
  language?: LanguageCode;
}

const DOCUMENT_IDS_QUERY_KEY = 'documentIds';

function documentIdsToQueryParam(documentIds: string[]): string {
  return documentIds.join(',');
}

// Phase 11: the classification slice owns these endpoints under
// `/assessments/document-classifications`. The POST is user-initiated retry
// (auto-classify on upload is event-driven server-side, not a client call).
const CLASSIFICATION_ENDPOINT = '/api/assessments/document-classifications';

/**
 * Re-runs classification for one or more documents (user-initiated retry
 * after a previous FAILED). Returns 202 with the submitted document IDs.
 *
 * Note: not called on first upload — the server auto-classifies via
 * `document.upload-confirmed.v1` and the client only polls.
 */
export const submitClassificationJob = async (
  request: ClassifyDocumentsRequest,
): Promise<ClassifyDocumentsAcceptedResponse> => {
  const response = await apiClient.post<ClassifyDocumentsAcceptedResponse>(
    CLASSIFICATION_ENDPOINT,
    request,
  );
  return response.data;
};

/**
 * Poll lightweight classification status for Document IDs (comma-separated query).
 */
export const fetchClassificationStatus = async (
  documentIds: string[],
): Promise<GetClassificationStatusResponse> => {
  const response = await apiClient.get<GetClassificationStatusResponse>(
    CLASSIFICATION_ENDPOINT,
    {
      params: { [DOCUMENT_IDS_QUERY_KEY]: documentIdsToQueryParam(documentIds) },
    },
  );
  return response.data;
};


/**
 * React Query hook for the retry-classification mutation.
 */
export const useClassifyDocuments = () => {
  return useMutation({
    mutationFn: submitClassificationJob,
  });
};
